// client/src/contexts/NetworkPolicyContext.tsx
// Fetches /api/my-ip after auth resolves and blocks the entire app if the user's
// IP is not in any whitelisted range (when policy enforcement is enabled).
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';

type NetworkPolicyState = {
  ready:       boolean;
  allowed:     boolean;
  detectedIps: string[];
  reason:      string;
};

const NetworkPolicyCtx = createContext<NetworkPolicyState>({
  ready:       false,
  allowed:     true,
  detectedIps: [],
  reason:      '',
});

export function useNetworkPolicy(): NetworkPolicyState {
  return useContext(NetworkPolicyCtx);
}

// ─── Blocked screen ───────────────────────────────────────────────────────────
function NetworkBlockedScreen({ detectedIps }: { detectedIps: string[] }) {
  return (
    <div style={{
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      minHeight:      '100vh',
      background:     '#0f1117',
      color:          '#e0e0e0',
      fontFamily:     'system-ui, sans-serif',
      padding:        '2rem',
      textAlign:      'center',
    }}>
      <div style={{
        width:        64,
        height:       64,
        borderRadius: '50%',
        background:   'rgba(255,107,107,.12)',
        border:       '2px solid rgba(255,107,107,.35)',
        display:      'flex',
        alignItems:   'center',
        justifyContent: 'center',
        marginBottom: 24,
        fontSize:     28,
      }}>
        🚫
      </div>

      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 10px', letterSpacing: '-.01em' }}>
        Network Access Restricted
      </h1>
      <p style={{ fontSize: 14, color: '#8b8fa8', maxWidth: 420, lineHeight: 1.6, margin: '0 0 28px' }}>
        Your current network is not in the approved IP whitelist. You must be connected
        to the corporate network or VPN (e.g. Zscaler) to access this application.
      </p>

      {detectedIps.length > 0 && (
        <div style={{
          background:   'rgba(255,255,255,.04)',
          border:       '1px solid rgba(255,255,255,.1)',
          borderRadius: 10,
          padding:      '14px 20px',
          marginBottom: 28,
          maxWidth:     480,
          width:        '100%',
          textAlign:    'left',
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: '#8b8fa8', marginBottom: 8 }}>
            Detected IP(s)
          </div>
          {detectedIps.map(ip => (
            <div key={ip} style={{ fontFamily: 'monospace', fontSize: 13, color: '#ff6b6b', padding: '2px 0' }}>
              {ip}
            </div>
          ))}
        </div>
      )}

      <p style={{ fontSize: 12, color: '#5a5d72', margin: 0 }}>
        If you believe this is an error, contact{' '}
        <a href="mailto:DataAnalyticsTeam@bmss.com" style={{ color: '#7aa2ff', textDecoration: 'none' }}>
          DataAnalyticsTeam@bmss.com
        </a>
      </p>
    </div>
  );
}

// ─── Provider ─────────────────────────────────────────────────────────────────
export function NetworkPolicyProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth();

  const [state, setState] = useState<NetworkPolicyState>({
    ready:       false,
    allowed:     true,
    detectedIps: [],
    reason:      '',
  });

  useEffect(() => {
    // Skip on the access-denied page to avoid redirect loops
    if (window.location.pathname === '/access-denied') {
      setState({ ready: true, allowed: true, detectedIps: [], reason: 'access-denied path' });
      return;
    }

    // Wait until auth has resolved with a known email
    if (!auth.ready || !auth.email) return;

    fetch('/api/my-ip', { credentials: 'include', cache: 'no-store' })
      .then(async r => {
        if (!r.ok) {
          // Fail open — don't block if the endpoint itself errors
          setState({ ready: true, allowed: true, detectedIps: [], reason: `HTTP ${r.status} — failed open` });
          return;
        }
        const data = await r.json();
        setState({
          ready:       true,
          allowed:     !!data.allowed,
          detectedIps: Array.isArray(data.detectedIps) ? data.detectedIps : [],
          reason:      data.reason || '',
        });
      })
      .catch(() => {
        // Network error — fail open
        setState({ ready: true, allowed: true, detectedIps: [], reason: 'Fetch error — failed open' });
      });
  }, [auth.ready, auth.email]);

  const value = useMemo(() => state, [state]);

  // While auth hasn't resolved yet, render children normally (auth handles its own loading)
  if (!auth.ready) {
    return <NetworkPolicyCtx.Provider value={value}>{children}</NetworkPolicyCtx.Provider>;
  }

  // Show a brief loading state only after auth is ready and we're waiting on the IP check
  if (auth.ready && auth.email && !state.ready) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', background: '#0f1117',
      }} />
    );
  }

  // Block if policy says no
  if (state.ready && !state.allowed) {
    return <NetworkBlockedScreen detectedIps={state.detectedIps} />;
  }

  return <NetworkPolicyCtx.Provider value={value}>{children}</NetworkPolicyCtx.Provider>;
}
